import assert from 'assert';
import Cookies from 'js-cookie';
import { SetterOrUpdater } from 'recoil';
import { checkAndGetNewAccessToken } from '../../api/checkAndGetNewAccessToken';
import axios from '../../common/axiosInterceptor';
import { Logger } from '../../common/Logger';
import { ACCESS_TOKEN_COOKIE_EXPIRE_MS } from '../../constants';
import { IUserAtom } from '../../recoil/atoms/user';
import { IUserSettings } from '../../recoil/selectors/permissionsSelector';
import { endpointEnum } from '../rest/makeAPIRequest';

export type TGetTokenReturn =
  | {
      success: true;
      expiresInTimestamp: number;
      accessToken: string;
      token: string; // TODO: remove after server and client releases
      refreshToken: string;
      userSettings: IUserSettings;
      logout: boolean;
    }
  | {
      success: false;
      message: string;
      logout: boolean;
    };

interface IRefreshTokenAjax {
  /** Recoil setter for user atom */
  setUserAtom: SetterOrUpdater<any>;
  /** do not log out if failed to refresh */
  suppressLogout?: boolean;
}

/**
 * Refreshes access-token if it has expired from getToken API
 * Update user atom if successful or clear user atom if failed
 */
export const refreshTokenAjax = async ({
  setUserAtom,
  suppressLogout,
}: IRefreshTokenAjax) => {
  let userFromLocalStorage: IUserAtom;
  try {
    userFromLocalStorage = JSON.parse(localStorage.getItem('user') as string);
    const {
      accessToken,
      refreshed,
      expiresInTimestamp,
    } = await checkAndGetNewAccessToken();

    assert(accessToken);

    // has logged in and access-token expired
    if (refreshed) {
      assert(expiresInTimestamp);
      // update user atom with new token
      setUserAtom({
        ...userFromLocalStorage,
        accessToken: accessToken,
      });

      return {
        success: true,
        loginUser: {
          ...userFromLocalStorage,
          accessToken: accessToken,
        } as IUserAtom,
        expiryDateInMs: new Date(expiresInTimestamp).getTime(),
        expiryDurationInMs: ACCESS_TOKEN_COOKIE_EXPIRE_MS,
      };
    }
  } catch (exception) {
    if (!suppressLogout) {
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      Cookies.remove('accessToken', { path: '/' });

      // clear user atom
      setUserAtom({});
    }

    //eng 7408 - do not log as error as this is expected behavior
    Logger.info(
      {
        message: 'Cannot refresh token',
        error: exception,
      },
      'handleRefreshToken'
    );

    // return response
    return {
      success: false,
      error: exception,
    };
  }

  // if uers atom empty and local-storage full then populate from local storage else do not change state
  setUserAtom(
    (state) =>
      !state?.accessToken // if state not set
        ? userFromLocalStorage // update user atom with localStorage
        : state // no-op: do not change state
  );

  // return response
  return {
    success: true,
    loginUser: userFromLocalStorage,
  };
};

/**
 * Retrieves new access token and userSettings using refresh token from /login
 * @throws {Error} does not throw error
 */
export async function getToken(props: {
  /** Refresh token gotten from /login */
  refreshToken: string | null;
}) {
  try {
    if (!props.refreshToken) {
      throw new Error('getToken.ts: refresh token empty ' + props.refreshToken);
    }

    const { data: response } = await axios.post<TGetTokenReturn>(
      `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/${endpointEnum.GET_TOKEN}`,
      {
        refreshToken: props.refreshToken,
      }
    );
    return response;
  } catch (error: any) {
    return {
      success: false,
      message: error?.message ? error.message : '',
      logout: false,
    };
  }
}
